import API_BASE from '../config';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import { PromptPicker } from '../components/PromptPicker';
import toast from "react-hot-toast";
import { creditAwareFetch, emitCreditsChanged } from '../hooks/creditAwareFetch';

const FIELDS = [
  { key: 'sector', label: 'Sector' },
  { key: 'company_size', label: 'Company size' },
  { key: 'geography', label: 'Geography' },
  { key: 'decision_maker_titles', label: 'Decision maker titles' },
  { key: 'buying_triggers', label: 'Buying triggers' },
  { key: 'disqualifiers', label: 'Disqualifiers' },
];


function PillInput({ pills, onChange, placeholder, colours, textColours }) {
  const [input, setInput] = useState('');
  const PILL_COLOURS = colours || ['#EBF8FF','#F0FFF4','#FAF5FF','#FFFBEB','#FFF5F5'];
  const TEXT_COLOURS = textColours || ['#2B6CB0','#276749','#553C9A','#B7791F','#742A2A'];

  function addPill(value) {
    const trimmed = value.trim().replace(/,$/, '').trim();
    if (!trimmed || pills.includes(trimmed)) return;
    onChange([...pills, trimmed]);
    setInput('');
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addPill(input);
    } else if (e.key === 'Backspace' && !input && pills.length > 0) {
      onChange(pills.slice(0, -1));
    }
  }

  function handleChange(e) {
    const val = e.target.value;
    if (val.endsWith(',')) {
      addPill(val);
    } else {
      setInput(val);
    }
  }

  function removePill(index) {
    onChange(pills.filter((_, i) => i !== index));
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', padding: '0.4rem 0.5rem', border: '1px solid #D4CFC9', borderRadius: '6px', backgroundColor: '#fff', minHeight: '38px', cursor: 'text' }}
      onClick={e => e.currentTarget.querySelector('input')?.focus()}>
      {pills.map((pill, i) => {
        const bg = PILL_COLOURS[i % PILL_COLOURS.length];
        const color = TEXT_COLOURS[i % TEXT_COLOURS.length];
        return (
          <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', backgroundColor: bg, color, fontSize: '0.72rem', fontWeight: '600', padding: '0.15rem 0.5rem', borderRadius: '20px', border: '1px solid ' + color + '44' }}>
            {pill}
            <button onClick={e => { e.stopPropagation(); removePill(i); }}
              style={{ background: 'none', border: 'none', color, cursor: 'pointer', padding: 0, fontSize: '0.75rem', lineHeight: 1, fontWeight: '700' }}>
              ×
            </button>
          </span>
        );
      })}
      <input
        value={input}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (input.trim()) addPill(input); }}
        placeholder={pills.length === 0 ? placeholder : ''}
        style={{ border: 'none', outline: 'none', fontSize: '0.78rem', color: '#1A1A1A', flex: 1, minWidth: '80px', backgroundColor: 'transparent', fontFamily: 'inherit', padding: '0.1rem 0' }}
      />
    </div>
  );
}

function ICP() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [discovery, setDiscovery] = useState(null);
  const [icp, setIcp] = useState(null);
  const [formData, setFormData] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ sector: '', company_size: '', geography: '', include_keywords: '', exclude_keywords: '' });
  const [filtersInitialised, setFiltersInitialised] = useState(false);

  useEffect(() => { fetchClient(); fetchDiscovery(); fetchICP(); }, [id]);

  // Pre-populate filters from ICP when ICP loads
  function initFilters(icpData) {
    if (!filtersInitialised && icpData) {
      setFilters({
        sector: icpData.sector || '',
        company_size: icpData.company_size || '',
        geography: icpData.geography || '',
        include_keywords: '',
        exclude_keywords: ''
      });
      setFiltersInitialised(true);
    }
  }

  async function handleFindCompanies(searchMore = false) {
    setSearching(true);
    setSearchError(null);
    if (!searchMore) { setSearchResults(null); setImportResult(null); setSelectedCompanies(new Set()); }
    try {
      const res = await creditAwareFetch(API_BASE + '/clients/' + id + '/icp-company-search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...filters, search_more: searchMore })
      });
      if (!res) return;
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      if (searchMore && searchResults) {
        setSearchResults(prev => ({ ...data, companies: [...prev.companies, ...data.companies] }));
      } else {
        setSearchResults(data);
      }
    } catch (e) {
      setSearchError(e.message);
      toast.error('Could not find companies: ' + e.message);
    }
    setSearching(false);
  }

  async function handleClearSearch() {
    const confirmed = window.confirm('Clear all search results? This will reset the search cache for this client and let you search fresh.');
    if (!confirmed) return;
    const toastId = toast.loading('Clearing search cache...');
    try {
      const res = await fetch(API_BASE + '/clients/' + id + '/icp-search-cache/clear', { method: 'POST' });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSearchResults(null);
      setSelectedCompanies(new Set());
      setImportResult(null);
      setSearchError(null);
      toast.success('Cleared ' + (data.cleared || 0) + ' cached results — ready for fresh search', { id: toastId });
    } catch (e) {
      toast.error('Could not clear: ' + e.message, { id: toastId });
    }
  }

  async function handleImportSelected() {
    if (!selectedCompanies.size) return;
    if (selectedCompanies.size > 10) {
      toast.error('Max 10 allowed per batch. You have ' + selectedCompanies.size + ' selected.');
      return;
    }
    setImporting(true);
    const importToastId = toast.loading(`Adding and enriching ${selectedCompanies.size} ${selectedCompanies.size === 1 ? 'company' : 'companies'}... this can take up to a minute`);
    try {
      const toImport = searchResults.companies.filter(c => selectedCompanies.has(c.domain));
      const res = await creditAwareFetch(API_BASE + '/clients/' + id + '/icp-company-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companies: toImport })
      });
      if (!res) return;
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setImportResult(data);
      const importedCount = data.imported || (data.companies && data.companies.length) || selectedCompanies.size;
      toast.success(`Imported ${importedCount} ${importedCount === 1 ? 'company' : 'companies'} to workspace`, { id: importToastId });
      setSelectedCompanies(new Set());
      setSearchResults(prev => ({
        ...prev,
        companies: prev.companies.map(c => selectedCompanies.has(c.domain) ? { ...c, already_in_workspace: true } : c)
      }));
    } catch (e) {
      setSearchError(e.message);
      toast.error('Could not import: ' + e.message, { id: importToastId });
    }
    setImporting(false);
  }

  async function fetchClient() {
    try { const res = await fetch(`${API_BASE}/clients/${id}`); setClient(await res.json()); } catch (err) {}
  }

  async function fetchDiscovery() {
    try { const res = await fetch(`${API_BASE}/clients/${id}/discovery`); setDiscovery(await res.json()); } catch (err) {}
  }

  async function fetchICP() {
    try {
      const res = await fetch(`${API_BASE}/clients/${id}/icp`);
      const data = await res.json();
      if (data) {
        setIcp(data);
        initFilters(data);
        setFormData({
          sector: data.sector || '',
          company_size: data.company_size || '',
          geography: data.geography || '',
          decision_maker_titles: data.decision_maker_titles || '',
          buying_triggers: data.buying_triggers || '',
          disqualifiers: data.disqualifiers || '',
        });
      }
    } catch (err) {}
  }

  async function saveICP(data, promptText) {
    const res = await creditAwareFetch(`${API_BASE}/clients/${id}/icp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, prompt_used: promptText || '' })
    });
    if (!res) return null; // 402 limit_reached — modal handled by creditAwareFetch
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  }

  async function handleGenerate() {
    setGenerating(true);
    setError(null);
    const toastId = toast.loading('Generating ICP with AI...');
    try {
      const res = await creditAwareFetch(`${API_BASE}/clients/${id}/generate-icp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt })
      });
      if (!res) return;
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setFormData(data);
      const saved = await saveICP(data, prompt);
      setIcp(saved);
      toast.success('ICP generated successfully', { id: toastId });
    } catch (err) {
      setError('Could not generate ICP. ' + err.message);
      toast.error('Could not generate ICP. ' + err.message, { id: toastId });
    }
    setGenerating(false);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await saveICP(formData, prompt);
      toast.success('ICP saved');
      navigate('/clients/' + id);
    } catch (err) {
      setError('Could not save ICP.');
      toast.error('Could not save ICP');
    }
    setSaving(false);
  }

  return (
    <div style={{ backgroundColor: '#FAFAF8', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }} data-color-mode="light">

      <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto', boxSizing: 'border-box' }}>

        <button onClick={() => navigate('/clients/' + id)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.83rem', cursor: 'pointer', padding: 0, marginBottom: '1.5rem' }}>
          ← {client ? client.name : 'Back'}
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' }}>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: '500', margin: '0 0 0.25rem', color: '#1A1A1A' }}>ICP</h1>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>{client ? client.name : ''}{icp ? ` · Version ${icp.version}` : ''}</p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {error && <span style={{ fontSize: '0.83rem', color: '#C53030' }}>{error}</span>}
            <button onClick={handleSave} disabled={saving} style={{ backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '500', opacity: saving ? 0.5 : 1 }}>
              {saving ? 'Saving...' : 'Save and return'}
            </button>
          </div>
        </div>

        {/* Generate panel */}
        <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #8B1A1A', borderRadius: '10px', padding: '1.25rem', marginBottom: '1.5rem' }}>
          <p style={{ margin: '0 0 0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#1A1A1A' }}>Generate with AI</p>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: '#888' }}>
            {discovery ? 'Discovery brief loaded — click generate to create an ICP, or add extra instructions below.' : 'No discovery brief found. Fill in the Discovery Brief first for better results.'}
          </p>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="Optional: add extra instructions, e.g. 'Focus on UK-based firms with 20-50 staff in the accounting sector'"
            rows={2}
            style={{ width: '100%', backgroundColor: '#FAFAF8', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.75rem', color: '#1A1A1A', fontSize: '0.875rem', boxSizing: 'border-box', resize: 'vertical', lineHeight: '1.5', fontFamily: 'inherit', marginBottom: '0.75rem', outline: 'none' }}
          />
          <div style={{ marginBottom: '0.75rem' }}>
            <PromptPicker type="ICP Generation" onSelect={text => setPrompt(prev => prev ? prev + '\n' + text : text)} />
          </div>
          <button onClick={handleGenerate} disabled={generating}
            style={{ backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '500', opacity: generating ? 0.5 : 1 }}>
            {generating ? 'Generating...' : 'Generate ICP'}
          </button>
        </div>

        {/* Find Companies Panel */}
        <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #276749', borderRadius: '10px', marginBottom: '1.5rem', overflow: 'hidden' }}>

          {/* Header row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: searchResults ? '0.5px solid #E8E4E0' : 'none' }}>
            <div>
              <p style={{ margin: '0 0 0.15rem', fontSize: '0.875rem', fontWeight: '500', color: '#1A1A1A' }}>Find companies matching this ICP</p>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#888' }}>Client Engine searches 50M+ companies and returns the best matches for your ICP.</p>
            </div>
            <button onClick={() => handleFindCompanies(false)} disabled={searching || !icp}
              style={{ backgroundColor: '#276749', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 1.25rem', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '500', opacity: searching || !icp ? 0.5 : 1, whiteSpace: 'nowrap', flexShrink: 0, marginLeft: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              {searching && !searchResults ? (
                <><span style={{ display: 'inline-block', width: '10px', height: '10px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />Searching...</>
              ) : 'Find companies →'}
            </button>
          </div>

          {searchError && <p style={{ margin: 0, padding: '0.5rem 1.25rem', fontSize: '0.78rem', color: '#C53030', borderBottom: '0.5px solid #E8E4E0' }}>{searchError}</p>}

          {/* Results area */}
          {searchResults && (
            <div style={{ display: 'grid', gridTemplateColumns: showFilters ? '280px 1fr' : '1fr', minHeight: '480px' }}>

              {/* Filter panel — shown only when toggled */}
              {showFilters && (
                <div style={{ borderRight: '0.5px solid #E8E4E0', padding: '1rem', backgroundColor: '#FAFAF8', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <p style={{ margin: 0, fontSize: '0.68rem', fontWeight: '700', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Refine search</p>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: '#888', marginBottom: '0.25rem', fontWeight: '500' }}>Sector</label>
                    <textarea value={filters.sector} onChange={e => setFilters(p => ({ ...p, sector: e.target.value }))} rows={3}
                      style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.6rem', fontSize: '0.75rem', color: '#1A1A1A', backgroundColor: '#fff', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit', resize: 'vertical', lineHeight: '1.5' }} />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: '#888', marginBottom: '0.25rem', fontWeight: '500' }}>Company size</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.4rem' }}>
                      {['1-10', '10-50', '50-200', '200-500', '500+'].map(size => (
                        <button key={size} onClick={() => setFilters(p => ({ ...p, company_size: p.company_size === size ? '' : size }))}
                          style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: '20px', border: '1px solid ' + (filters.company_size === size ? '#276749' : '#D4CFC9'), backgroundColor: filters.company_size === size ? '#F0FFF4' : '#fff', color: filters.company_size === size ? '#276749' : '#4A5568', cursor: 'pointer', fontWeight: filters.company_size === size ? '600' : '400' }}>
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: '#888', marginBottom: '0.25rem', fontWeight: '500' }}>Geography</label>
                    <textarea value={filters.geography} onChange={e => setFilters(p => ({ ...p, geography: e.target.value }))} rows={2}
                      style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.6rem', fontSize: '0.75rem', color: '#1A1A1A', backgroundColor: '#fff', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit', resize: 'vertical', lineHeight: '1.5' }} />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: '#888', marginBottom: '0.25rem', fontWeight: '500' }}>Must include</label>
                    <PillInput
                      pills={filters.include_keywords ? filters.include_keywords.split(',').map(k => k.trim()).filter(Boolean) : []}
                      onChange={pills => setFilters(p => ({ ...p, include_keywords: pills.join(',') }))}
                      placeholder="Type and press Enter..."
                      colours={['#EBF8FF','#F0FFF4','#FAF5FF','#FFFBEB']}
                      textColours={['#2B6CB0','#276749','#553C9A','#B7791F']}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: '#888', marginBottom: '0.25rem', fontWeight: '500' }}>Exclude</label>
                    <PillInput
                      pills={filters.exclude_keywords ? filters.exclude_keywords.split(',').map(k => k.trim()).filter(Boolean) : []}
                      onChange={pills => setFilters(p => ({ ...p, exclude_keywords: pills.join(',') }))}
                      placeholder="Type and press Enter..."
                      colours={['#FFF5F5','#FED7D7']}
                      textColours={['#742A2A','#C53030']}
                    />
                  </div>

                  <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '0.4rem', paddingTop: '0.5rem', borderTop: '0.5px solid #E8E4E0' }}>
                    <button onClick={() => handleFindCompanies(false)} disabled={searching}
                      style={{ backgroundColor: '#276749', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem', fontSize: '0.82rem', cursor: 'pointer', fontWeight: '500', opacity: searching ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                      {searching ? <><span style={{ display: 'inline-block', width: '8px', height: '8px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />Searching...</> : 'Search again'}
                    </button>
                    <button onClick={() => setFilters({ sector: icp?.sector || '', company_size: icp?.company_size || '', geography: icp?.geography || '', include_keywords: '', exclude_keywords: '' })}
                      style={{ fontSize: '0.72rem', color: '#888', background: 'none', border: 'none', padding: '0.2rem', cursor: 'pointer', textDecoration: 'underline' }}>
                      Reset to ICP defaults
                    </button>
                  </div>
                </div>
              )}

              {/* Results column */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Results toolbar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.65rem 1rem', borderBottom: '0.5px solid #E8E4E0', flexWrap: 'wrap', gap: '0.4rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#888' }}>
                      {searchResults.companies.length} companies{searchResults.total_cached > 25 ? ' · ' + searchResults.total_cached + ' seen total' : ''}{selectedCompanies.size > 0 ? ' · ' + selectedCompanies.size + ' selected' : ''}
                    </p>
                    <button onClick={() => setShowFilters(p => !p)}
                      style={{ fontSize: '0.72rem', color: showFilters ? '#8B1A1A' : '#276749', background: 'none', border: '1px solid ' + (showFilters ? '#FEB2B2' : '#9AE6B4'), borderRadius: '20px', padding: '0.15rem 0.5rem', cursor: 'pointer', fontWeight: '500' }}>
                      {showFilters ? 'Hide filters' : 'Refine search'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <button onClick={() => handleFindCompanies(true)} disabled={searching}
                      style={{ fontSize: '0.72rem', color: '#276749', background: 'none', border: '1px solid #9AE6B4', borderRadius: '20px', padding: '0.15rem 0.5rem', cursor: 'pointer', opacity: searching ? 0.5 : 1 }}>
                      {searching ? 'Searching...' : 'Find more'}
                    </button>
                    <button onClick={() => setSelectedCompanies(new Set(searchResults.companies.filter(c => !c.already_in_workspace).slice(0, 10).map(c => c.domain)))}
                      style={{ fontSize: '0.72rem', color: '#4A5568', background: 'none', border: '1px solid #D4CFC9', borderRadius: '20px', padding: '0.15rem 0.5rem', cursor: 'pointer' }}>
                      Select 10
                    </button>
                    {selectedCompanies.size > 0 && (
                      <button onClick={() => setSelectedCompanies(new Set())}
                        style={{ fontSize: '0.72rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '20px', padding: '0.15rem 0.5rem', cursor: 'pointer' }}>
                        Deselect
                      </button>
                    )}
                    <button onClick={handleClearSearch}
                      style={{ fontSize: '0.72rem', color: '#8B1A1A', background: 'none', border: '1px solid #D4CFC9', borderRadius: '20px', padding: '0.15rem 0.5rem', cursor: 'pointer' }}>
                      Clear search
                    </button>
                    {selectedCompanies.size > 0 && (
                      <button onClick={handleImportSelected} disabled={importing}
                        style={{ fontSize: '0.72rem', color: '#fff', background: '#276749', border: 'none', borderRadius: '20px', padding: '0.15rem 0.65rem', cursor: 'pointer', fontWeight: '600', opacity: importing ? 0.6 : 1 }}>
                        {importing ? 'Adding...' : 'Add ' + selectedCompanies.size + ' to workspace'}
                      </button>
                    )}
                  </div>
                </div>

                {importResult && (
                  <div style={{ padding: '0.5rem 1rem', backgroundColor: '#F0FFF4', borderBottom: '0.5px solid #9AE6B4', fontSize: '0.78rem', color: '#276749' }}>
                    {importResult.added} {importResult.added === 1 ? 'company' : 'companies'} added{importResult.skipped > 0 ? ', ' + importResult.skipped + ' skipped (already in workspace)' : ''}.
                  </div>
                )}

                {/* Scrollable results list */}
                <div style={{ overflowY: 'auto', maxHeight: '420px', padding: '0.5rem' }}>
                  {searchResults.companies.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                      <p style={{ margin: 0, fontSize: '0.85rem' }}>No results. Try adjusting your filters.</p>
                    </div>
                  ) : searchResults.companies.map(company => (
                    <div key={company.domain}
                      onClick={() => {
                        if (company.already_in_workspace) return;
                        const next = new Set(selectedCompanies);
                        if (next.has(company.domain)) next.delete(company.domain);
                        else next.add(company.domain);
                        setSelectedCompanies(next);
                      }}
                      style={{
                        display: 'flex', gap: '0.6rem', padding: '0.65rem 0.75rem', marginBottom: '0.35rem',
                        backgroundColor: company.already_in_workspace ? '#FAFAF8' : selectedCompanies.has(company.domain) ? '#F0FFF4' : '#fff',
                        border: '0.5px solid ' + (selectedCompanies.has(company.domain) ? '#9AE6B4' : '#E8E4E0'),
                        borderLeft: '3px solid ' + (company.already_in_workspace ? '#D4CFC9' : selectedCompanies.has(company.domain) ? '#276749' : '#E8E4E0'),
                        borderRadius: '6px', cursor: company.already_in_workspace ? 'default' : 'pointer',
                        opacity: company.already_in_workspace ? 0.5 : 1
                      }}>
                      <input type="checkbox" checked={selectedCompanies.has(company.domain)} disabled={company.already_in_workspace} onChange={() => {}}
                        style={{ flexShrink: 0, marginTop: '3px', accentColor: '#276749' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.1rem', flexWrap: 'wrap' }}>
                          <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '500', color: '#1A1A1A' }}>{company.name}</p>
                          {company.already_in_workspace && <span style={{ fontSize: '0.65rem', color: '#276749', backgroundColor: '#F0FFF4', border: '1px solid #9AE6B4', borderRadius: '20px', padding: '0.1rem 0.35rem', fontWeight: '500' }}>In workspace</span>}
                        </div>
                        <p style={{ margin: '0 0 0.2rem', fontSize: '0.68rem', color: '#AAA' }}>{company.domain}</p>
                        {company.description && (
                          <p style={{ margin: 0, fontSize: '0.75rem', color: '#4A5568', lineHeight: '1.45', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {company.description.replace(/^Summary:\n?/,  '').substring(0, 180)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Initial state — no results yet */}
          {!searchResults && !searching && icp && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>
              <p style={{ margin: '0 0 0.25rem', fontSize: '0.88rem', fontWeight: '500', color: '#1A1A1A' }}>Ready to search</p>
              <p style={{ margin: 0, fontSize: '0.78rem' }}>Click "Find companies" to search using your ICP. Results preview here before adding to workspace.</p>
            </div>
          )}
          {searching && !searchResults && (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
              <span style={{ display: 'inline-block', width: '18px', height: '18px', border: '2px solid #276749', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              Searching 50M+ companies...
            </div>
          )}
        </div>

        {/* ICP Fields */}
        {/* ICP Fields */}
        {/* ICP Fields */}
        {/* ICP Fields */}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingBottom: '3rem' }}>
          {FIELDS.map(field => (
            <div key={field.key} style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #8B1A1A', borderRadius: '10px', padding: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <label style={{ fontSize: '0.83rem', color: '#4A5568', fontWeight: '500' }}>{field.label}</label>
                {editingField !== field.key ? (
                  <button onClick={() => setEditingField(field.key)}
                    style={{ background: 'none', border: '1px solid #D4CFC9', borderRadius: '20px', color: '#4A5568', fontSize: '0.75rem', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>
                    Edit
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => setEditingField(null)}
                      style={{ background: '#276749', border: 'none', borderRadius: '20px', color: 'white', fontSize: '0.75rem', padding: '0.2rem 0.6rem', cursor: 'pointer', fontWeight: '500' }}>
                      Done
                    </button>
                    <button onClick={() => setEditingField(null)}
                      style={{ background: 'none', border: '1px solid #D4CFC9', borderRadius: '20px', color: '#888', fontSize: '0.75rem', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              {editingField === field.key ? (
                <MDEditor value={formData[field.key] || ''} onChange={val => setFormData(prev => ({ ...prev, [field.key]: val || '' }))} preview="edit" height={200} />
              ) : (
                <div style={{ fontSize: '0.875rem', color: formData[field.key] ? '#1A1A1A' : '#BBB', lineHeight: '1.6', whiteSpace: 'pre-wrap', minHeight: '1.5rem' }}>
                  {formData[field.key] || 'Not yet defined.'}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ICP;
